import { useMutation } from '@apollo/client/react'
import {
  Button,
  FailureNotice,
  FieldMessage,
  RadioGroup,
  RadioGroupItem,
  TextInput,
  Textarea,
  useTranslate,
} from '@athena/ui'
import { useState } from 'react'

import { PracticeDocument, UpdateQuestionDocument } from '../../graphql/generated/operations.js'
import { useUserErrorText } from '../../lib/user-errors.js'
import type { AuthoredQuestionShape } from './authoredQuestion.js'

export function QuestionEditor({
  question,
  practiceId,
  onCancel,
  onSaved,
}: {
  question: AuthoredQuestionShape
  practiceId: string
  onCancel: () => void
  onSaved: () => void
}) {
  const t = useTranslate()
  const userErrorText = useUserErrorText()
  const [prompt, setPrompt] = useState(question.prompt)
  const [explanation, setExplanation] = useState(question.explanation ?? '')
  const [options, setOptions] = useState(question.options.map((option) => ({ ...option })))
  const [acceptedAnswers, setAcceptedAnswers] = useState([...question.acceptedAnswers])
  const [correctOrder, setCorrectOrder] = useState([...question.correctOrder])
  const [pairs, setPairs] = useState(question.pairs.map((pair) => ({ ...pair })))
  const [error, setError] = useState<string>()
  const [failure, setFailure] = useState<unknown>()
  const [updateQuestion, { loading }] = useMutation(UpdateQuestionDocument, {
    refetchQueries: [{ query: PracticeDocument, variables: { id: practiceId } }],
    awaitRefetchQueries: true,
  })

  function moveOption(optionId: string, delta: -1 | 1) {
    setCorrectOrder((current) => {
      const next = [...current]
      const from = next.indexOf(optionId)
      const to = from + delta
      if (from < 0 || to < 0 || to >= next.length) return current
      ;[next[from], next[to]] = [next[to]!, next[from]!]
      return next
    })
  }

  function addOption() {
    const id = `new-${crypto.randomUUID()}`
    setOptions((values) => [...values, { id, label: '', text: '', isCorrect: false }])
    if (question.type === 'ASSOCIATION') setCorrectOrder((values) => [...values, id])
  }

  function removeOption(optionId: string) {
    setOptions((values) => values.filter((option) => option.id !== optionId))
    setCorrectOrder((values) => values.filter((id) => id !== optionId))
  }

  function addPair() {
    setPairs((values) => [
      ...values,
      { id: `new-${crypto.randomUUID()}`, position: values.length, leftText: '', rightText: '' },
    ])
  }

  async function save() {
    setError(undefined)
    setFailure(undefined)
    try {
      const payload = (
        await updateQuestion({
          variables: {
            input: {
              questionId: question.id,
              prompt,
              explanation: explanation.trim() || null,
              options: options.map(({ id, text, isCorrect }) => ({ id, text, isCorrect })),
              acceptedAnswers,
              correctOrder,
              pairs: pairs.map(({ id, leftText, rightText }) => ({ id, leftText, rightText })),
            },
          },
        })
      ).data?.updateQuestion
      if (payload?.practice) return onSaved()
      setError(
        payload?.userErrors[0]
          ? userErrorText(payload.userErrors[0])
          : t('practice.questionEdit.failed'),
      )
    } catch (cause) {
      setFailure(cause)
    }
  }

  return (
    <form
      noValidate
      className="flex flex-col gap-5"
      onSubmit={(event) => {
        event.preventDefault()
        void save()
      }}
    >
      <Textarea
        className="min-h-28 resize-none"
        label={t('practice.questionEdit.prompt')}
        value={prompt}
        onChange={(event) => setPrompt(event.target.value)}
      />

      {question.type === 'SHORT_ANSWER' ? (
        <div className="flex flex-col gap-3">
          {acceptedAnswers.map((answer, index) => (
            <div key={index} className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
              <TextInput
                label={t('practice.questionEdit.acceptedAnswer', [index + 1])}
                value={answer}
                onChange={(event) =>
                  setAcceptedAnswers((values) =>
                    values.map((value, item) => (item === index ? event.target.value : value)),
                  )
                }
              />
              {acceptedAnswers.length > 1 ? (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() =>
                    setAcceptedAnswers((values) => values.filter((_, item) => item !== index))
                  }
                >
                  {t('common.remove')}
                </Button>
              ) : null}
            </div>
          ))}
          <Button
            type="button"
            variant="secondary"
            onClick={() => setAcceptedAnswers((values) => [...values, ''])}
          >
            {t('practice.questionEdit.addAnswer')}
          </Button>
        </div>
      ) : null}

      {question.type === 'MULTIPLE_CHOICE' || question.type === 'TRUE_FALSE' ? (
        <RadioGroup
          value={options.find((option) => option.isCorrect)?.id ?? null}
          onValueChange={(id) =>
            setOptions((values) =>
              values.map((option) => ({ ...option, isCorrect: option.id === id })),
            )
          }
          className="flex flex-col gap-3"
          aria-label={t('practice.questionEdit.correctAnswer')}
        >
          {options.map((option, index) => (
            <div
              key={option.id}
              className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-end gap-2"
            >
              <RadioGroupItem value={option.id} />
              <TextInput
                label={t('practice.questionEdit.option', [index + 1])}
                value={option.text}
                onChange={(event) =>
                  setOptions((values) =>
                    values.map((value) =>
                      value.id === option.id ? { ...value, text: event.target.value } : value,
                    ),
                  )
                }
              />
              {question.type === 'MULTIPLE_CHOICE' && options.length > 2 ? (
                <Button type="button" variant="secondary" onClick={() => removeOption(option.id)}>
                  {t('common.remove')}
                </Button>
              ) : null}
            </div>
          ))}
          {question.type === 'MULTIPLE_CHOICE' ? (
            <Button type="button" variant="secondary" onClick={addOption}>
              {t('practice.questionEdit.addOption')}
            </Button>
          ) : null}
        </RadioGroup>
      ) : null}

      {question.type === 'ASSOCIATION' ? (
        <div className="flex flex-col gap-3">
          <p className="text-body font-semibold text-text">
            {t('practice.questionEdit.correctOrder')}
          </p>
          {correctOrder.map((optionId, index) => {
            const option = options.find((item) => item.id === optionId)!
            return (
              <div
                key={optionId}
                className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto_auto_auto] sm:items-end"
              >
                <TextInput
                  label={t('practice.questionEdit.orderItem', [index + 1])}
                  value={option.text}
                  onChange={(event) =>
                    setOptions((values) =>
                      values.map((value) =>
                        value.id === optionId ? { ...value, text: event.target.value } : value,
                      ),
                    )
                  }
                />
                <Button
                  type="button"
                  variant="secondary"
                  disabled={index === 0}
                  onClick={() => moveOption(optionId, -1)}
                >
                  {t('common.moveUp')}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  disabled={index === correctOrder.length - 1}
                  onClick={() => moveOption(optionId, 1)}
                >
                  {t('common.moveDown')}
                </Button>
                {options.length > 2 ? (
                  <Button type="button" variant="secondary" onClick={() => removeOption(optionId)}>
                    {t('common.remove')}
                  </Button>
                ) : null}
              </div>
            )
          })}
          <Button type="button" variant="secondary" onClick={addOption}>
            {t('practice.questionEdit.addOption')}
          </Button>
        </div>
      ) : null}

      {question.type === 'MATCHING' ? (
        <div className="flex flex-col gap-4">
          {pairs.map((pair, index) => (
            <div
              key={pair.id}
              className="grid gap-3 rounded-sm border border-border p-4 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] sm:items-end"
            >
              <TextInput
                label={t('practice.questionEdit.leftItem', [index + 1])}
                value={pair.leftText}
                onChange={(event) =>
                  setPairs((values) =>
                    values.map((value) =>
                      value.id === pair.id ? { ...value, leftText: event.target.value } : value,
                    ),
                  )
                }
              />
              <TextInput
                label={t('practice.questionEdit.rightItem', [index + 1])}
                value={pair.rightText}
                onChange={(event) =>
                  setPairs((values) =>
                    values.map((value) =>
                      value.id === pair.id ? { ...value, rightText: event.target.value } : value,
                    ),
                  )
                }
              />
              {pairs.length > 2 ? (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() =>
                    setPairs((values) => values.filter((value) => value.id !== pair.id))
                  }
                >
                  {t('common.remove')}
                </Button>
              ) : null}
            </div>
          ))}
          <Button type="button" variant="secondary" onClick={addPair}>
            {t('practice.questionEdit.addPair')}
          </Button>
        </div>
      ) : null}

      <Textarea
        className="min-h-24 resize-none"
        label={t('practice.questionEdit.explanation')}
        optional
        value={explanation}
        onChange={(event) => setExplanation(event.target.value)}
      />
      {error ? <FieldMessage tone="error">{error}</FieldMessage> : null}
      {failure === undefined ? null : (
        <FailureNotice
          title={t('practice.questionEdit.failed')}
          error={failure}
          retrying={loading}
          onRetry={() => void save()}
        />
      )}
      <div className="flex flex-wrap gap-3">
        <Button type="submit" loading={loading} loadingText={t('common.saving')}>
          {t('common.save')}
        </Button>
        <Button type="button" variant="secondary" disabled={loading} onClick={onCancel}>
          {t('common.cancel')}
        </Button>
      </div>
    </form>
  )
}
